import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PropertyCard } from "../components/PropertyCard";
import { ATLASBuyer } from "../components/ATLASBuyer";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { MagnifyingGlass, FunnelSimple } from "@phosphor-icons/react";
import { isEnabled } from "../lib/config";

const MAX_PRICE = 30000000;

export default function PropertiesPage() {
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState(params.get("q") || "");
  const [op, setOp] = useState(params.get("operation") || "todas");
  const [type, setType] = useState(params.get("property_type") || "todos");
  const [city, setCity] = useState(params.get("city") || "");
  const [minBed, setMinBed] = useState(params.get("min_bedrooms") || "0");
  const [minBath, setMinBath] = useState(params.get("min_bathrooms") || "0");
  const [sortBy, setSortBy] = useState(params.get("sort_by") || "newest");
  const [priceRange, setPriceRange] = useState([
    Number(params.get("min_price")) || 0,
    Number(params.get("max_price")) || MAX_PRICE,
  ]);

  const fetchProps = () => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (op !== "todas") p.operation = op;
    if (type !== "todos") p.property_type = type;
    if (city) p.city = city;
    if (minBed && minBed !== "0") p.min_bedrooms = minBed;
    if (minBath && minBath !== "0") p.min_bathrooms = minBath;
    if (sortBy && sortBy !== "newest") p.sort_by = sortBy;
    if (priceRange[0] > 0) p.min_price = priceRange[0];
    if (priceRange[1] < MAX_PRICE) p.max_price = priceRange[1];
    api.get("/properties", { params: p })
      .then(({ data }) => setProperties(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProps(); /* eslint-disable-next-line */ }, []);

  const applyFilters = (e) => {
    e?.preventDefault();
    const next = {};
    if (q) next.q = q;
    if (op !== "todas") next.operation = op;
    if (type !== "todos") next.property_type = type;
    if (city) next.city = city;
    if (minBed && minBed !== "0") next.min_bedrooms = minBed;
    if (priceRange[0] > 0) next.min_price = String(priceRange[0]);
    if (priceRange[1] < MAX_PRICE) next.max_price = String(priceRange[1]);
    setParams(next);
    fetchProps();
  };

  const resetFilters = () => {
    setQ(""); setOp("todas"); setType("todos"); setCity(""); setMinBed("0");
    setPriceRange([0, MAX_PRICE]);
    setParams({});
    setTimeout(fetchProps, 0);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Catálogo</p>
        <h1 className="font-heading text-4xl lg:text-5xl mt-2 tracking-tight" data-testid="properties-title">Todas las propiedades</h1>
      </div>

      {isEnabled("atlasConversationalSearch") && (
        <div className="mb-10">
          <ATLASBuyer />
        </div>
      )}

      <div className="grid lg:grid-cols-[300px_1fr] gap-8">
        {/* Filters */}
        <aside className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6 h-fit lg:sticky lg:top-24" data-testid="filters-panel">
          <div className="flex items-center gap-2 mb-6">
            <FunnelSimple size={20} weight="bold" className="text-[#C8A24A]" />
            <h3 className="font-heading text-xl">Filtros</h3>
          </div>

          <form onSubmit={applyFilters} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#C8A24A]/80">Búsqueda</label>
              <div className="mt-2 relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C8A24A]/50" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Palabra clave" className="pl-9" data-testid="filter-q" />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#C8A24A]/80">Operación</label>
              <Select value={op} onValueChange={setOp}>
                <SelectTrigger className="mt-2" data-testid="filter-operation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="venta">Venta</SelectItem>
                  <SelectItem value="renta">Renta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#C8A24A]/80">Tipo</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-2" data-testid="filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="casa">Casa</SelectItem>
                  <SelectItem value="departamento">Departamento</SelectItem>
                  <SelectItem value="terreno">Terreno</SelectItem>
                  <SelectItem value="oficina">Oficina</SelectItem>
                  <SelectItem value="local">Local</SelectItem>
                  <SelectItem value="bodega">Bodega</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#C8A24A]/80">Ciudad</label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ej. Guadalajara" className="mt-2" data-testid="filter-city" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#C8A24A]/80">Recámaras mín.</label>
              <Select value={minBed} onValueChange={setMinBed}>
                <SelectTrigger className="mt-2" data-testid="filter-bedrooms"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["0", "1", "2", "3", "4", "5"].map(n => (
                    <SelectItem key={n} value={n}>{n === "0" ? "Cualquiera" : `${n}+`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#C8A24A]/80">Precio (MXN)</label>
              <div className="mt-4 px-1">
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  min={0}
                  max={MAX_PRICE}
                  step={100000}
                  data-testid="filter-price-slider"
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-[#C8A24A]/70">
                <span>${priceRange[0].toLocaleString("es-MX")}</span>
                <span>${priceRange[1].toLocaleString("es-MX")}{priceRange[1] === MAX_PRICE ? "+" : ""}</span>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]" data-testid="filter-apply-btn">
                Aplicar
              </Button>
              <Button type="button" variant="outline" onClick={resetFilters} data-testid="filter-reset-btn">
                Limpiar
              </Button>
            </div>
          </form>
        </aside>

        {/* Results */}
        <div>
          <p className="text-sm text-[#C8A24A]/70 mb-6" data-testid="properties-count">
            {loading ? "Cargando..." : `${properties.length} propiedad${properties.length !== 1 ? "es" : ""}`}
          </p>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 overflow-hidden">
                  <div className="aspect-[4/3] bg-[#1A1A1A] animate-pulse" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 bg-[#1A1A1A] rounded animate-pulse" />
                    <div className="h-4 bg-[#1A1A1A] rounded w-2/3 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : properties.length === 0 ? (
            <div className="bg-[#1A1A1A] rounded-2xl border border-dashed border-[#C8A24A]/20 p-16 text-center">
              <p className="font-heading text-2xl mb-2">Sin resultados</p>
              <p className="text-sm text-[#C8A24A]/70">Prueba ajustando los filtros.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
              {properties.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
